import React from 'react';
import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import DSRadio from '../../../design-system/DSRadio';
import DSRadioGroup from '../../../design-system/DSRadioGroup';
import DSFormControlLabel from '../../../design-system/DSFormControlLabel';
import DSFormControl from '../../../design-system/DSFormControl';
import { DSTypography } from '../../../design-system/Typography';
import { SoundMode } from '../../../api/klydo';

export const DEFAULT_PLAY_ONCE_FREQUENCY = 10;
const FREQUENCY_OPTIONS = [5, 10, 15, 30] as const;

type KlydoSoundPlaybackRadioProps = {
  soundMode: SoundMode;
  playOnceFrequency: number | null;
  onSoundModeChange: (mode: SoundMode) => void;
  onPlayOnceFrequencyChange: (frequency: number) => void;
  disabled?: boolean;
};

const KlydoSoundPlaybackRadio = ({
  soundMode,
  playOnceFrequency,
  onSoundModeChange,
  onPlayOnceFrequencyChange,
  disabled = false,
}: KlydoSoundPlaybackRadioProps) => {
  const { spacing, palette } = useTheme();

  const effectiveSoundMode = soundMode ?? SoundMode.OFF;

  const handleModeChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    const mode = value as SoundMode;
    onSoundModeChange(mode);
  };

  const handleFrequencyChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    onPlayOnceFrequencyChange(Number(value));
  };

  return (
    <DSFormControl disabled={disabled}>
      <DSRadioGroup value={effectiveSoundMode} onChange={handleModeChange}>
        <DSFormControlLabel
          value={SoundMode.OFF}
          control={<DSRadio size="small" />}
          label="None"
          sx={{
            '.MuiFormControlLabel-label': { color: palette.text.secondary },
          }}
        />
        <DSFormControlLabel
          value={SoundMode.ON}
          control={<DSRadio size="small" />}
          label="Continuous"
          sx={{
            '.MuiFormControlLabel-label': { color: palette.text.secondary },
          }}
        />
        <DSFormControlLabel
          value={SoundMode.FIRST_LOOP}
          control={<DSRadio size="small" />}
          label="Random"
          sx={{
            '.MuiFormControlLabel-label': { color: palette.text.secondary },
          }}
        />
      </DSRadioGroup>

      {effectiveSoundMode === SoundMode.FIRST_LOOP && (
        <DSBox sx={{ pl: spacing(4) }}>
          <DSRadioGroup
            value={String(playOnceFrequency ?? 10)}
            onChange={handleFrequencyChange}
          >
            {FREQUENCY_OPTIONS.map((minutes) => (
              <DSFormControlLabel
                key={minutes}
                value={String(minutes)}
                control={<DSRadio size="small" />}
                label={
                  <DSTypography variant="body2" color={palette.text.secondary}>
                    Once in {minutes} minutes window
                  </DSTypography>
                }
              />
            ))}
          </DSRadioGroup>
        </DSBox>
      )}
    </DSFormControl>
  );
};

export default KlydoSoundPlaybackRadio;
